import { Component, Inject, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { finalize, takeUntil } from 'rxjs/operators';

import { CompositeProductModel, FamilyModel, ProductStatusesModel } from 'common/src/modules/products/models';
import { ProductsService } from 'common/src/modules/products/products.service';
import { ProductTypes } from 'common/src/modules/products/product-types';
import { ToasterService } from 'common/src/modules/ui-components/toaster';
import { CommonModalsActionsEnum, DangerModalComponent } from 'common/src/modules/modals/modals-common';
import { NavBarBtnActionsEnum } from 'common/src/models';
import { CreateProductTypeUnitModalComponent } from 'common/src/modules/modals/modals-products';
import { ProductStatusEnum } from 'projects/workspace/src/app/products-two-level/enums';
import { FundsWriteOffEnum } from 'projects/workspace/src/app/fixed-assets/enums';
import { ProductUnitModel } from 'projects/workspace/src/app/shared/models';
import { ProductUnitApiService } from 'projects/workspace/src/app/shared/services';
import { GeneralLedgerApiService } from 'projects/workspace/src/app/gl-accounts/services/general-ledger-api.service';
import { GlPlanModeEnum, GlProductTypeProductModel } from 'projects/workspace/src/app/gl-accounts/models';

@Component({
  selector: 'rnpl-create-general-product-modal',
  templateUrl: './create-general-product-modal.component.html',
})
export class CreateGeneralProductModalComponent implements OnDestroy {

  public entityTree: Array<FamilyModel>;
  private form: FormGroup;
  public productTypes = ProductTypes;
  public fundsWriteOffEnum = FundsWriteOffEnum;
  public unitsList: string[] = [];

  readonly destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  readonly submitRequest$: BehaviorSubject<boolean> = new BehaviorSubject(null);

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private productsService: ProductsService,
    private productUnitApiService: ProductUnitApiService,
    private generalLedgerApiService: GeneralLedgerApiService,
    private toasterService: ToasterService,
    public dialogRef: MatDialogRef<CreateGeneralProductModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      customProduct: CompositeProductModel;
      familyId: string;
      productType: ProductTypes;
      // TODO: rules — use the general ledger API in all places and remove this flag
      generalLedgerMode?: boolean;
      glAccountsPlanMode?: GlPlanModeEnum;
      readOnly?: boolean;
      entityTree: Array<FamilyModel>;
      availableForIncomingDocs: boolean;
      availableForIncomingDocsDisabled: boolean;
      availableForPayments: boolean;
      availableForPaymentsDisabled: boolean;
    }
  ) {
    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });

    this.initForm();

    if (this.data.customProduct) {
      this.patchForm(this.data.customProduct);
      this.getProductUnits();

      if (this.isDeletedStatus || this.data.readOnly) {
        this.form.disable({emitEvent: false});
      }
    }

    if (!this.data.customProduct && this.data.familyId) {
      this.familyIdControl.patchValue(+this.data.familyId, {emitEvent: false});
    }

    if (this.data.entityTree) {
      this.entityTree = this.data.entityTree;
    } else {
      this.getEntityTree();
    }

    if (this.data.availableForIncomingDocs) {
      this.availableForIncomingDocsControl.patchValue(this.data.availableForIncomingDocs);
    }
    if (this.data.availableForPayments) {
      this.availableForPaymentsControl.patchValue(this.data.availableForPayments);
    }

    if (this.data.availableForIncomingDocsDisabled) {
      this.availableForIncomingDocsControl.disable();
    }
    if (this.data.availableForPaymentsDisabled) {
      this.availableForPaymentsControl.disable();
      if (this.familyIdControl.value) {
        this.familyIdControl.disable();
      }
    }

    if (this.data.productType) {
      this.entityFormControl.patchValue(this.data.productType);
    }
  }

  public getEntityTree(): void {
    const requestParams = new HttpParams().set('products_type', 'general');

    this.productsService.getFamiliesTree(requestParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        this.entityTree = response.data.map(itm => ({
          ...itm,
          id: +itm.id,
        }));
        this.sortEntityTree();
      });
  }

  public sortEntityTree(): void {
    const visibleItems = this.entityTree
      .filter((family) => family.is_visible_for_list)
      .sort((a, b) => a.title.localeCompare(b.title));
    // const hiddenItems = this.entityTree
    //   .filter((family) => !family.is_visible_for_list)
    //   .sort((a, b) => a.title.localeCompare(b.title));

    // this.entityTree = [...visibleItems, ...hiddenItems];
    this.entityTree = [...visibleItems];
  }

  public initForm(): void {
    this.form = this.fb.group({
      familyId: [null, Validators.required],
      name: [null, Validators.required],
      entityForm: [null, Validators.required],
      unitType: ['pcs', Validators.required],
      availableForIncomingDocs: [true],
      availableForPayments: [false],
      // salesAvailable: [false],
      accountingSettings: this.fb.group({
        vatRateByDefault: [null],
        glAccountIdPurchaseDebit: [null, Validators.required],
        usedUpAllowed: [false],
        glAccountWriteOffDebit: [],
        usedUpByDefault: [false],
        longTermPeriodOfUse: [false],
        fundsWriteOffMethod: [null, Validators.required],
        periodOfUse: [2, [Validators.required, Validators.min(1.01)]],
        fundsWriteOffRate: [null, [Validators.required, Validators.min(0.01), Validators.max(99.99)]],
      }),
    });

    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.data.readOnly) { return; }

        if (this.longTermPeriodOfUseControl.value) {
          this.periodOfUseControl.enable({emitEvent: false});
          this.usedUpByDefaultControl.patchValue(false, {emitEvent: false});
          this.fundsWriteOffMethodControl.enable({emitEvent: false});
          this.usedUpByDefaultControl.disable({emitEvent: false});
          if (this.fundsWriteOffMethodControl.value === FundsWriteOffEnum.DEGRESSIVE) {
            this.fundsWriteOffRateControl.enable({emitEvent: false});
          } else {
            this.fundsWriteOffRateControl.disable({emitEvent: false});
          }
        } else {
          this.periodOfUseControl.disable({emitEvent: false});
          this.fundsWriteOffMethodControl.disable({emitEvent: false});
          this.usedUpByDefaultControl.enable({emitEvent: false});
          this.fundsWriteOffRateControl.disable({emitEvent: false});
        }

        if (this.usedUpByDefaultControl.value) {
          this.longTermPeriodOfUseControl.disable({emitEvent: false});
        } else {
          this.longTermPeriodOfUseControl.enable({emitEvent: false});
        }

        if (this.usedUpAllowedControl.value) {
          this.glAccountWriteOffDebitControl.setValidators(Validators.required);
        } else {
          this.longTermPeriodOfUseControl.enable({emitEvent: false});
          this.usedUpByDefaultControl.patchValue(false, {emitEvent: false});
          this.glAccountWriteOffDebitControl.clearValidators();
        }
      });
  }

  public entityFormChanged(): void {
    this.unitTypeControl.patchValue('pcs');
    this.usedUpAllowedControl.patchValue(false);
    // this.salesAvailableControl.patchValue(false);
    this.usedUpByDefaultControl.patchValue(false);
    this.longTermPeriodOfUseControl.patchValue(false);
    this.periodOfUseControl.patchValue(2);
    this.glAccountWriteOffDebitControl.patchValue(null);
    this.getProductUnits();
  }

  public updateField(fieldValue: any, fieldName: string): void {
    if (!this.data.customProduct || this.isDeletedStatus || this.data.readOnly) { return; }

    if (this.isGeneralLedgerMode) {
      this.generalLedgerApiService.updateProductTypeProduct(+this.data.customProduct.id, {fieldName, fieldValue})
        .subscribe();
      return;
    }

    this.productsService.updateProductField(this.data.customProduct.id, {fieldName, fieldValue})
      .subscribe((product: CompositeProductModel) => this.patchForm(product));
  }

  private patchForm(product: CompositeProductModel): void {
    this.form.patchValue(product);
  }

  public getProductUnits(): void {
    this.productUnitApiService.getUnits$(this.entityFormControl.value)
      .pipe(takeUntil(this.destroy$))
      .subscribe((unitsList: ProductUnitModel[]) => this.unitsList = unitsList.map(u => u.name));
  }

  public createProductUnit(): void {
    const dialog = this.dialog.open(CreateProductTypeUnitModalComponent, {
      minWidth: '480px',
      maxWidth: '480px',
      panelClass: ['dragged-panel', 'sm-modal'],
      backdropClass: 'backdrop-none',
      disableClose: true,
      data: {
        createUnitFunction$: (name, allowFractionalValues) => {
          return this.productUnitApiService.createProductUnit(this.entityFormControl.value, name, allowFractionalValues);
        }
      }
    });

    dialog.afterClosed().subscribe((unit: ProductUnitModel) => {
      if (unit) {
        this.getProductUnits();

        if (this.data.customProduct) {
          this.updateField(unit.name, 'unitType');
        } else {
          this.unitTypeControl.patchValue(unit.name);
        }
      }
    });
  }

  public close(product?: CompositeProductModel|GlProductTypeProductModel|boolean): void {
    this.dialogRef.close(product);
  }

  public deleteCustomProduct(): void {
    const dialog = this.dialog.open(DangerModalComponent, {
      data: {
        title: 'MODAL.DELETE',
        message: 'GL_ACCOUNTS.GENERAL_PRODUCT_DELETE_MSG',
        confirmBtnText: 'BUTTON.DELETE',
        confirmBtnIcon: 'trash-2',
      },
    });

    dialog.afterClosed().subscribe((res) => {
      if (res === CommonModalsActionsEnum.CONFIRM && !this.isGeneralLedgerMode) {
        this.changeStatus(NavBarBtnActionsEnum.DELETE);
      }
      if (res === CommonModalsActionsEnum.CONFIRM && this.isGeneralLedgerMode) {
        this.generalLedgerApiService.deleteProductTypeProduct(+this.data.customProduct.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.toasterService.notify({type: 'success', message: 'GL_ACCOUNTS.GENERAL_PRODUCT_DELETED_SUCCESS'});
            this.close(this.data.customProduct);
          });
      }
    });
  }

  public deletePermanentlyCustomProduct(): void {
    const dialog = this.dialog.open(DangerModalComponent, {
      data: {
        title: 'MODAL.DELETE_PERMANENTLY',
        message: 'GL_ACCOUNTS.GENERAL_PRODUCT_DELETE_PERMANENTLY_MSG',
        confirmBtnText: 'BUTTON.DELETE',
        confirmBtnIcon: 'trash-2',
      },
    });

    dialog.afterClosed().subscribe((res) => {
      if (res === CommonModalsActionsEnum.CONFIRM) {
        this.productsService.deleteProducts([+this.data.customProduct.id])
          .pipe(takeUntil(this.destroy$))
          .subscribe( () => {
            if (!this.isGeneralLedgerMode) {
              this.close(this.data.customProduct);
            }
          }, error => {
            this.toasterService.notify({type: 'error', message: error.error.message});
          });
      }
    });
  }

  public activateCustomProduct(): void {
    this.changeStatus(NavBarBtnActionsEnum.ACTIVATE);
  }

  private changeStatus(status: NavBarBtnActionsEnum): void {
    const statuses: ProductStatusesModel = {
      statuses: [{
        product_id: this.data.customProduct.id as number,
        status
      }]
    };
    this.productsService.changeProductsStatuses(statuses)
      .pipe(takeUntil(this.destroy$))
      .subscribe( () => {
        this.close(this.data.customProduct);
      }, error => {
        this.toasterService.notify({type: 'error', message: error.error.message});
      });
  }

  public submit(): void {
    this.form.updateValueAndValidity();
    this.form.markAllAsTouched();

    if (this.form.invalid) { return; }
    if (this.submitRequest$.getValue()) { return; }

    this.submitRequest$.next(true);

    const productData = {
      general_products_model: {
        family_id: this.familyIdControl.value
      },
      name: this.nameControl.value,
      entityForm: this.entityFormControl.value,
      unitType: this.unitTypeControl.value,
      salesAvailable: this.entityFormControl.value !== ProductTypes.GOODS,
      ...this.form.getRawValue().accountingSettings,
      availableForIncomingDocs: this.availableForIncomingDocsControl.value,
      availableForPayments: this.availableForPaymentsControl.value,
    };

    const request$: Observable<CompositeProductModel|GlProductTypeProductModel> = this.isGeneralLedgerMode
      ? this.generalLedgerApiService.createProductTypeProduct(
          {...productData, familyId: this.familyIdControl.value},
          this.data.glAccountsPlanMode
        )
      : this.productsService.createCompositeProduct(this.familyIdControl.value, this.productTypes.GENERAL, productData);

    request$
      .pipe(
        finalize(() => this.submitRequest$.next(false)),
        takeUntil(this.destroy$))
      .subscribe((product: CompositeProductModel|GlProductTypeProductModel) => {
        this.close(product);
        this.toasterService.notify({type: 'success', message: 'SUCCESS_CREATED.PRODUCT'});
      }, (error) => {
        if (!this.isGeneralLedgerMode) {
          this.toasterService.notify({type: 'error', message: error.error.message});
        }
      });
  }

  get isGeneralLedgerMode(): boolean {
    return !!this.data.generalLedgerMode;
  }

  get isDeletedStatus(): boolean {
    return this.data.customProduct && this.data.customProduct.status === ProductStatusEnum.DELETED;
  }

  get familyIdControl(): FormControl { return this.form.get('familyId') as FormControl; }
  get nameControl(): FormControl { return this.form.get('name') as FormControl; }
  get entityFormControl(): FormControl { return this.form.get('entityForm') as FormControl; }
  get unitTypeControl(): FormControl { return this.form.get('unitType') as FormControl; }
  get availableForIncomingDocsControl(): FormControl { return this.form.get('availableForIncomingDocs') as FormControl; }
  get availableForPaymentsControl(): FormControl { return this.form.get('availableForPayments') as FormControl; }
  // get salesAvailableControl(): FormControl { return this.form.get('salesAvailable') as FormControl; }
  get vatRateByDefaultControl(): FormControl { return this.form.get('accountingSettings').get('vatRateByDefault') as FormControl; }
  get glAccountIdPurchaseDebitControl(): FormControl { return this.form.get('accountingSettings').get('glAccountIdPurchaseDebit') as FormControl; }
  get usedUpAllowedControl(): FormControl { return this.form.get('accountingSettings').get('usedUpAllowed') as FormControl; }
  get glAccountWriteOffDebitControl(): FormControl { return this.form.get('accountingSettings').get('glAccountWriteOffDebit') as FormControl; }
  get usedUpByDefaultControl(): FormControl { return this.form.get('accountingSettings').get('usedUpByDefault') as FormControl; }
  get longTermPeriodOfUseControl(): FormControl { return this.form.get('accountingSettings').get('longTermPeriodOfUse') as FormControl; }
  get periodOfUseControl(): FormControl { return this.form.get('accountingSettings').get('periodOfUse') as FormControl; }
  get fundsWriteOffMethodControl(): FormControl { return this.form.get('accountingSettings').get('fundsWriteOffMethod') as FormControl; }
  get fundsWriteOffRateControl(): FormControl { return this.form.get('accountingSettings').get('fundsWriteOffRate') as FormControl; }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

}
